import { apiService } from './api';

/**
 * Get paginated list of media files
 * 
 * @param {Object} params - Query parameters
 * @param {Number} params.page - Page number (default: 1)
 * @param {Number} params.limit - Number of items per page (default: 10)
 * @param {String} params.sort - Sort field (default: '-createdAt')
 * @param {String} params.type - Filter by media type (image, video, etc.)
 * @param {String} params.status - Filter by media status
 * @param {String} params.mediaId - Filter by media ID
 * @param {String} params.messageId - Filter by message ID
 * @param {String} params.search - Search by filename/original name
 */
export const getMediaList = async (params = {}) => {
    try {
        const response = await apiService.fetchWithAuth('/api/media', {
            method: 'GET',
            params
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Get a media item by ID
 * 
 * @param {String} id - Media ID
 */
export const getMediaById = async (id) => {
    try {
        const response = await apiService.fetchWithAuth(`/api/media/${id}`, {
            method: 'GET'
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Delete a media item by ID
 * 
 * @param {String} id - Media ID
 */
export const deleteMedia = async (id) => {
    try {
        const response = await apiService.fetchWithAuth(`/api/media/${id}`, {
            method: 'DELETE'
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Redownload a media item from WhatsApp
 * 
 * @param {String} id - MongoDB document ID
 * @param {String} mediaId - WhatsApp media ID to redownload (if available)
 */
export const redownloadMedia = async (id, mediaId = null) => {
    try {
        const response = await apiService.fetchWithAuth(`/api/media/${id}/redownload`, {
            method: 'POST',
            body: JSON.stringify({ mediaId })
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Force delete a media item by ID (removes both file and database record)
 * 
 * @param {String} id - MongoDB document ID
 */
export const forceDeleteMedia = async (id) => {
    try {
        const response = await apiService.fetchWithAuth(`/api/media/${id}/force`, {
            method: 'DELETE'
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Upload a media file
 * 
 * @param {File} file - The file to upload
 * @param {Object} metadata - Additional metadata
 * @param {String} metadata.name - Name for the file
 * @param {String} metadata.description - Description for the file
 */
export const uploadMedia = async (file, metadata = {}) => {
    try {
        const formData = new FormData();
        formData.append('file', file);

        if (metadata.name) {
            formData.append('name', metadata.name);
        }

        if (metadata.description) {
            formData.append('description', metadata.description);
        }

        const token = localStorage.getItem('token');
        const baseUrl = apiService.getBaseUrl();

        const response = await fetch(`${baseUrl}/api/upload-media`, {
            method: 'POST',
            headers: {
                Authorization: token ? `Bearer ${token}` : '',
                // Don't set Content-Type header, let browser set it with boundary
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Failed to upload media');
        }

        return await response.json();
    } catch (error) {
        console.error('Media upload error:', error);
        throw error;
    }
}; 